import {
  intranetAbsenceNoteResponseSchema,
  type SaveIntranetAbsenceNoteRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';
import { intranetChatConversationsKey } from '@/products/intranet/features/intranet/use-intranet-chat';
import { intranetQueryKey } from '@/products/intranet/features/intranet/use-intranet';

const okSchema = z.object({ ok: z.literal(true) });

export const intranetAbsenceNoteKey = [...intranetQueryKey, 'absence-note'] as const;
const colleagueProfilesKey = [...intranetQueryKey, 'colleagues'] as const;

export function useIntranetAbsenceNote(enabled: boolean) {
  return useQuery({
    queryKey: intranetAbsenceNoteKey,
    queryFn: () => apiFetch('/intranet/absence-note', intranetAbsenceNoteResponseSchema),
    enabled,
  });
}

export function useSaveIntranetAbsenceNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveIntranetAbsenceNoteRequest) =>
      apiFetch('/intranet/absence-note', intranetAbsenceNoteResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => refreshAbsenceSurfaces(queryClient),
  });
}

export function useRemoveIntranetAbsenceNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/intranet/absence-note', okSchema, {
        method: 'DELETE',
      }),
    onSuccess: () => refreshAbsenceSurfaces(queryClient),
  });
}

function refreshAbsenceSurfaces(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: intranetAbsenceNoteKey });
  void queryClient.invalidateQueries({ queryKey: intranetChatConversationsKey });
  void queryClient.invalidateQueries({ queryKey: colleagueProfilesKey });
}
